<template>
  <net-item
    v-if="ctx.sceneNode"
    class="scene-chart__container"
    item-class="scene-chart"
    :uid="nodeUid"
    :size="size"
    :node-uid="nodeUid"
    :class="{
      '-selected': selected,
      '-editable': editable,
    }"
    :min-width="100"
    :min-height="100 / size.width * size.height"
    :grid-size="1"
    :selected="selected"
    :clickable="clickable"
    :locked="ctx.sceneNode.locked"
    @select="select($event)"
  >
    <div v-if="ctx.chart && ctx.chart.chartSettings.titleVisible" class="chart__header" :style="{ fontSize: titleFontSize + 'px' }">
      <net-input-text
        ref="titleInput"
        :id="`scene-chart-title-${ctx.chart.id}`"
        :value="ctx.sceneNode.title ?? title"
        :disabled="!editable"
        :placeholder="title"
        :style="{ color: textColor, textAlign: ctx.sceneNode?.textAlign || 'left', fontSize: titleFontSize + 'px' }"
        class="chart__title scene-chart__title"
        :position="{ x: size.x, y: size.y }"
        :max-lines="1"
        :text-options="{
          textColor: ctx.sceneNode?.textColor,
          textAlign: ctx.sceneNode?.textAlign,
          fontSize: titleFontSize,
        }"
        @update:text-option="onTextOptionUpdate"
        @update:value="updateTitle($event)"
        @enter-edit-mode="emit('update:tool', 'textedit')"
        @leave-edit-mode="emit('update:tool', 'pointer')"
        @pointerdown="onTextPointerDown($event)"
      >
        <span v-if="ctx.sceneNode.title">{{ title }}</span>
        <span v-else>
          <span v-for="part in formattedTitle" :key="part.text" :style="part.style || ''">
            {{ part.text }}
          </span>
        </span>
      </net-input-text>
    </div>

    <Chart
      v-if="ctx.chart"
      :key="'scene-chart-chart-' + props.nodeUid"
      :title="ctx.sceneNode.title"
      :editable="editable"
      :chart-item="ctx.chart"
      :width="size.width"
      :height="size.height - titleHeight"
      class="scene-chart__main"
      :hoverable="!editable"
      :override-settings="{ titleVisible: false }"
      :class="{ '-editable': editable }"
      @update:title="updateTitle($event)"
    >
      <template #empty-icon="{ icon }">
        <lucide-icon v-if="icon" :name="icon" :style="{ fontSize: Math.min(size.width, size.height) / 4 + 'px' }" />
      </template>

      <!-- <template #legend="{ legendData, settings }">
        <div class="scene-chart__legend">
          <ChartLegend
            :legend="legendData"
            :settings="settings"
          ></ChartLegend>
        </div>
      </template> -->
    </Chart>

    <scene-chart-popup-menu
      v-if="popupOpen && ctx.chart"
      :node-uid="nodeUid"
      :chart-id="ctx.chart.id"
      :x="size.x + size.width"
      :y="size.y + size.height"
      @select="select"
      @close="closePopupMenu"
    />
    
  </net-item>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import eventHub from '@/helpers/utility/Events';

import { useSceneChart } from './useSceneChart';
import { useChartTitle } from '../charts/useChartTitle';
import { useChartData, type ChartItemOptions } from '../charts/useChartData';
import { useSceneNodesStore } from '../scene/sceneNodes.store';
import { useSceneTree } from '../scene/sceneTree';

import LucideIcon from '@core/ui/LucideIcon.vue';
import NetItem from '@editor/net/NetItem.vue';
import Chart from '@editor/items/charts/Chart.vue';
import SceneChartPopupMenu from './SceneChartPopupMenu.vue';
import NetInputText from '@editor/net/NetInputText.vue';

import type { Uid } from '@/helpers/utility/Id';
import type { CanvasSelection } from '@editor/net/NetCanvasProps';
import type { RotatableRect, SceneNodeItem } from '../scene/SceneNodeItem';
import type { TextOptions } from '@editor/net/netTextToolbarContext';
import type { NetToolKey } from '@editor/net/NetTools';
import type { Handleable } from '@core/types/Events';
import { onUnmounted } from 'vue';

const props = defineProps<{
  nodeUid: Uid<"sceneNode">;
  size: RotatableRect;
  selection: CanvasSelection;
  editable: boolean;
  clickable: boolean;
}>();

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"sceneNode"> }): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "update:tool", tool: NetToolKey): void;
}>();

const ctx = useSceneChart(() => props.nodeUid);
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();

// ====================
// SELECTION

const selected = computed(() => {
  return props.selection.selected.has(props.nodeUid);
});

function select(item: { uid: Uid<"sceneNode"> }) {
  if (!props.editable) return;
  emit("select", item);
}

// ====================
// COLOR

const textColor = computed(() => {
  if (ctx.sceneNode?.textColor) {
    return ctx.sceneNode.textColor;
  }
  return "var(--color-text)";
});

// ====================
// TITLE

const chartOptions = computed((): ChartItemOptions => ({
  uid: ctx.chart?.uid ?? "chart_-1",
  data: ctx.chart?.data ?? {},
  dataToggles: ctx.chart?.dataToggles,
  itemConfigs: ctx.chart?.itemConfigs,
  chartType: ctx.chart?.chartType,
  chartSettings: ctx.chart?.chartSettings,
}));
const {
  chartAxisConfig,
  selectedSimulations,
  selectedItems,
  availableItems,
  dataToggles,
} = useChartData(chartOptions);
const { formattedTitle, title } = useChartTitle(() => ctx.sceneNode.title, () => ({
  singleSimulation: chartAxisConfig.value?.singleSimulation,
  selectedSimulations: selectedSimulations.value,
  selectedItems: selectedItems.value,
  availableItems: availableItems.value,
  dataToggles: dataToggles.value,
}));

function updateTitle(title: string | null) {
  if (!props.editable) return;
  if (!ctx.sceneNode) return;
  ctx.updateKey("title", title);
}

const sceneFontSize = computed(() => {
  return sceneTree.activeScene.fontSize;
});
const titleFontSize = computed(() => {
  if (ctx.sceneNode?.fontSizeRelative != null) {
    return sceneFontSize.value * ctx.sceneNode.fontSizeRelative;
  }
  return sceneFontSize.value;
});

const titleHeight = computed(() => {
  if (!ctx.chart?.chartSettings.titleVisible) return 0;
  return 3 * titleFontSize.value;
});

function onTextOptionUpdate<K extends keyof TextOptions>(k: K, value: TextOptions[K]) {
  if (!ctx.sceneNode) return;
  if (k === "fontSize") {
    // fontSize is not directly stored on sceneNode, but calculated from fontSizeRelative and sceneFontSize
    const relativeSize = ((value as number | null) ?? sceneFontSize.value) / sceneFontSize.value;
    console.log("new relative size", relativeSize);
    sceneNodeStore.updateKey(ctx.sceneNode.id, "fontSizeRelative", relativeSize);
    return;
  }
  if (k in ctx.sceneNode) {
    const key = k as keyof (SceneNodeItem | TextOptions);
    sceneNodeStore.updateKey(ctx.sceneNode.id, key, value as any);
  }
}


function onTextPointerDown(event: PointerEvent & Handleable) {
  if (!props.editable) return;
  if (event.hasBeenHandled) return;
  if (event.shiftKey) return;

  event.hasBeenHandled = true;
  event.handledBy = "SceneChart";

  select({ uid: props.nodeUid });
}

// ====================
// POPUP MENU

const popupOpen = ref(false);

function openPopupMenu() {
  popupOpen.value = true;
}

function closePopupMenu() {
  popupOpen.value = false;
}

function onPopupmenuRequest(uid: string) {
  if (props.nodeUid === uid || uid === ctx.chart?.uid) {
    openPopupMenu();
  }
}

eventHub.on("popupmenu-request", onPopupmenuRequest);
onUnmounted(() => {
  eventHub.off("popupmenu-request", onPopupmenuRequest);
});

</script>

<style lang="scss">
.scene-chart {
  position: relative;

  &__main {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  &__title {
    .net-input-text__rendered {
      & > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .net-input-text__rendered.is-empty {
      opacity: 1;
      font-style: normal;
    }
  }

  &__empty {
    width: 100%;
    height: 100%;
    border: 1px dashed var(--color-border);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__legend {
    position: absolute;
    padding: 1rem;
    background-color: var(--color-background-deep);
  }
}
</style>
